import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import AuthTabs from './components/AuthTabs';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import SocialLoginButtons from './components/SocialLoginButtons';
import ForgotPasswordModal from './components/ForgotPasswordModal';
import TrustIndicators from './components/TrustIndicators';
import Icon from '../../components/AppIcon';

const LoginRegistration = () => {
  const [activeTab, setActiveTab] = useState('login');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Mock credentials for different user types
  const mockCredentials = {
    admin: { email: 'admin@drutravels.com', password: 'admin1234' },
    agent: { email: 'agent@drutravels.com', password: 'agent1234' },
    user: { email: 'user@drutravels.com', password: 'user1234' }
  };

  useEffect(() => {
    // Check if user is already authenticated
    const token = localStorage.getItem('authToken');
    if (token) {
      navigate('/user-dashboard', { replace: true });
    }
  }, [navigate]);

  const simulateAuthDelay = () => {
    return new Promise(resolve => setTimeout(resolve, 1500));
  };

  const handleLogin = async (formData) => {
    setIsLoading(true);
    setError('');

    try {
      await simulateAuthDelay();

      // Check against mock credentials
      const userType = Object.keys(mockCredentials).find(type => 
        mockCredentials[type].email === formData.email && 
        mockCredentials[type].password === formData.password
      );

      if (userType) {
        // Simulate successful login
        const mockToken = `mock_jwt_token_${userType}_${Date.now()}`;
        const userData = {
          id: `${userType}_001`,
          name: userType === 'admin' ? 'Admin User' : 
                userType === 'agent' ? 'Travel Agent' : 'John Doe',
          email: formData.email,
          role: userType,
          permissions: userType === 'admin' ? ['all'] : 
                      userType === 'agent' ? ['bookings', 'support', 'analytics'] : 
                      ['dashboard', 'bookings', 'support']
        };

        localStorage.setItem('authToken', mockToken);
        localStorage.setItem('userData', JSON.stringify(userData));

        // Redirect to intended page or dashboard
        const redirectTo = location.state?.from || '/user-dashboard';
        navigate(redirectTo, { replace: true });
      } else {
        setError('Invalid email or password. Please check your credentials and try again.');
      }
    } catch (err) {
      setError('Login failed. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (formData) => {
    setIsLoading(true);
    setError('');

    try {
      await simulateAuthDelay();

      // Simulate successful registration
      const mockToken = `mock_jwt_token_user_${Date.now()}`;
      const userData = {
        id: `user_${Date.now()}`,
        name: formData.fullName,
        email: formData.email,
        role: 'user',
        permissions: ['dashboard', 'bookings', 'support']
      };

      localStorage.setItem('authToken', mockToken);
      localStorage.setItem('userData', JSON.stringify(userData));

      navigate('/user-dashboard', { replace: true });
    } catch (err) {
      setError('Registration failed. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (provider) => {
    setIsLoading(true);
    setError('');

    try {
      await simulateAuthDelay();

      // Simulate successful social login
      const mockToken = `mock_jwt_token_social_${provider}_${Date.now()}`;
      const userData = {
        id: `social_${provider}_${Date.now()}`,
        name: provider === 'google' ? 'Google User' : 'Facebook User',
        email: `${provider}user@example.com`,
        role: 'user',
        permissions: ['dashboard', 'bookings', 'support'],
        provider
      };

      localStorage.setItem('authToken', mockToken);
      localStorage.setItem('userData', JSON.stringify(userData));

      navigate('/user-dashboard', { replace: true });
    } catch (err) {
      setError(`${provider} login failed. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (email) => {
    await simulateAuthDelay();
    // Simulate sending reset email
    return Promise.resolve();
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="pt-16 min-h-screen flex">
        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            {/* Logo and Welcome */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Plane" size={24} color="white" />
                </div>
                <span className="text-2xl font-heading font-bold text-foreground">
                  Dru Travels
                </span>
              </div>
              <h1 className="text-2xl font-heading font-semibold text-foreground mb-2">
                {activeTab === 'login' ? 'Welcome Back' : 'Join Dru Travels'}
              </h1>
              <p className="text-muted-foreground">
                {activeTab === 'login' ?'Discover amazing destinations across Tamil Nadu and India' :'Start your journey with India\'s trusted travel partner'
                }
              </p>
            </div>

            {/* Auth Tabs */}
            <AuthTabs activeTab={activeTab} onTabChange={setActiveTab} />

            {/* Auth Forms */}
            <div className="bg-card rounded-lg shadow-soft border border-border p-6 mb-6">
              {activeTab === 'login' ? (
                <LoginForm
                  onLogin={handleLogin}
                  isLoading={isLoading}
                  error={error}
                />
              ) : (
                <RegisterForm
                  onRegister={handleRegister}
                  isLoading={isLoading}
                  error={error}
                />
              )}
            </div>

            {/* Social Login */}
            <div className="bg-card rounded-lg shadow-soft border border-border p-6 mb-6">
              <SocialLoginButtons
                onSocialLogin={handleSocialLogin}
                isLoading={isLoading}
              />
            </div>

            
          </div>
        </div>

        {/* Trust Indicators Sidebar */}
        <div className="hidden lg:block w-80 bg-muted/30 p-6">
          <div className="sticky top-24">
            <h3 className="text-lg font-heading font-semibold text-foreground mb-6">
              Why Choose Dru Travels?
            </h3>
            <TrustIndicators />
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        onSubmit={handleForgotPassword}
        isLoading={isLoading}
      />
    </div>
  );
};

export default LoginRegistration;